/* eslint-disable max-len */
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from 'src/environments/environment';
import { PaginationParams, toHttpParams } from '../model/api/pagination-params';
import { resources } from './api';
import {
	BoardUserData, BoardUsersInfoData, BoardUsersListExportResponseData, BoardUsersListImportResponseData, BoardUsersListsRespData, DataRest, IdDataRest,
	PaginatedBoardUsers} from '../model/api/data-rest';
import { BoardUser, BoardUserDelete, BoardUsersExportList, isBadge } from '../model/board-user';


@Injectable({
	providedIn: 'root'
})
export class BoardUsersService {
	constructor(
		private http: HttpClient
	) {
	}

	getBoardUsers(deviceId: number, nopagination: boolean, paging?: PaginationParams): Observable<PaginatedBoardUsers> {
		let params;
		if (paging) {
			params = toHttpParams(paging);
		} else {
			params = new HttpParams();
		}
		if (nopagination) {
			params = params.set('nopagination', true);
		}
		return this.http.get<PaginatedBoardUsers>(
			`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('boardUsers')}`,
			{ params }
		);
	}

	getBoardUsersInfo(instanceId: number, language?: string): Observable<BoardUsersInfoData> {
		let params = new HttpParams(); // .set('cached', 'true');
		if (language) {
			params = params.set('language', language);
		}
		return this.http.get<BoardUsersInfoData>(
			`${environment.apiUrl}${resources.get('automations')}/${instanceId}${resources.get('boardUsers')}${resources.get('info')}`,
			{ params }
		);
	}

	deleteAllBoardUsers(deviceId: number): Observable<DataRest> {
		return this.http.delete<DataRest>(`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('boardUsers')}`);
	}

	deleteBoardUser(deviceId: number, position: number, code: string): Observable<DataRest> {
		let params = new HttpParams();
		params = params.set('position', position.toString());
		params = params.set('code', code);
		return this.http.delete<DataRest>(
			`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('boardUsers')}`,
			{ params }
		);
	}

	deleteBoardUserSelected(deviceId: number, boardUsers: BoardUserDelete[]): Observable<DataRest> {
		return this.http.post<IdDataRest>(
			`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('boardUsers')}${resources.get('deletetask')}`,
			{ BoardUsers: boardUsers }
		);
	}

	addBoardUser(deviceId: number, boardUser: BoardUser): Observable<IdDataRest> {
		return this.http.post<IdDataRest>(`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('boardUsers')}`, boardUser);
	}

	editBoardUser(instanceId: number, boardUser: BoardUser, isCodeGroup: boolean): Observable<IdDataRest> {
		if (isCodeGroup) {
			const { Type, TypeDescription, CodeBase, GroupName } = boardUser;
			return this.http.patch<IdDataRest>(
				`${environment.apiUrl}${resources.get('automations')}/${instanceId}${resources.get('boardUsers')}${resources.get('groups')}`,
				{ Type, TypeDescription, CodeBase, GroupName }
			);
		} else {
			const { Position, Mode, Name, TypeDescription, Autom1Cmd, Autom2Cmd, Autom3Cmd, Autom4Cmd, GroupId, DateFrom, DateTo } = boardUser;
			if (isBadge(boardUser.Type)) {
				return this.http.patch<IdDataRest>(
					`${environment.apiUrl}${resources.get('v2')}${resources.get('automations')}/${instanceId}${resources.get('boardUsers')}`,
					{ Position, Mode, Name, TypeDescription, Autom1Cmd, Autom2Cmd, Autom3Cmd, Autom4Cmd, GroupId, DateFrom, DateTo }
				);
			}
			return this.http.patch<IdDataRest>(
				`${environment.apiUrl}${resources.get('v2')}${resources.get('automations')}/${instanceId}${resources.get('boardUsers')}`,
				{ Position, Mode, Name, Autom1Cmd, Autom2Cmd, Autom3Cmd, Autom4Cmd, GroupId, DateFrom, DateTo }
			);
		}
	}

	syncBoardUsers(deviceId: number): Observable<IdDataRest> {
		return this.http.get<IdDataRest>(
			`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('boardUsers')}${resources.get('synctask')}`
		);
	}

	acquireBoardUser(deviceId: number): Observable<BoardUserData> {
		return this.http.get<BoardUserData>(
			`${environment.apiUrl}${resources.get('v2')}${resources.get('automations')}/${deviceId}${resources.get('boardUsers')}${resources.get('acquire')}`
		);
	}

	/* board users import list task */
	addBoardUsers(deviceId: number, boardUsers: BoardUser[]): Observable<IdDataRest> {
		return this.http.post<IdDataRest>(
			`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('boardUsers')}${resources.get('addtask')}`,
			{ BoardUsers: boardUsers }
		);
	}

	getBoardusersLists(userId: number, modelId: number, paging?: PaginationParams): Observable<BoardUsersListsRespData> {
		let params = new HttpParams();
		if (paging) {
			params = toHttpParams(paging);
		}
		if (userId) {
			params = params.set('userId', userId.toString());
		}
		if (modelId) {
			params = params.set('modelId', modelId.toString());
		}
		return this.http.get<BoardUsersListsRespData>(
			`${environment.apiUrl}${resources.get('v1')}${resources.get('devices')}${resources.get('boardUsers')}`, { params }
		);
	}

	exportBoardUsersList(userId: number, boardUsersExportList: BoardUsersExportList): Observable<BoardUsersListExportResponseData> {
		let params = new HttpParams();
		if (userId) {
			params = params.set('userId', userId.toString());
		}
		return this.http.post<BoardUsersListExportResponseData>(
			`${environment.apiUrl}${resources.get('v1')}${resources.get('devices')}${resources.get('boardUsers')}${resources.get('export')}`, boardUsersExportList, { params }
		);
	}

	editBoardUsersList(userId: number, boardUsersEditList: BoardUsersExportList, listId: number): Observable<BoardUsersListExportResponseData> {
		let params = new HttpParams();
		if (userId) {
			params = params.set('userId', userId.toString());
		}
		return this.http.patch<BoardUsersListExportResponseData>(
			`${environment.apiUrl}${resources.get('v1')}${resources.get('devices')}${resources.get('boardUsers')}/${listId}`, boardUsersEditList, { params }
		);
	}

	deleteBoarduserList(userId: number, listId: number, version: string): Observable<BoardUsersListExportResponseData> {
		let params = new HttpParams();
		if (userId) {
			params = params.set('userId', userId.toString());
		}
		if (version) {
			params = params.set('version', version);
		}
		return this.http.delete<BoardUsersListExportResponseData>(
			`${environment.apiUrl}${resources.get('v1')}${resources.get('devices')}${resources.get('boardUsers')}/${listId}`, { params }
		);
	}

	importBoardUsersList(userId: number, deviceId: number, listId: number): Observable<BoardUsersListImportResponseData> {
		let params = new HttpParams();
		if (userId) {
			params = params.set('userId', userId.toString());
		}
		return this.http.post<BoardUsersListImportResponseData>(
			`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('boardUsers')}/${listId}${resources.get('import')}`, {}, { params }
		);
	}
}
